import { AlertTriangle, ExternalLink, HelpCircle, Plus, Settings2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Button } from '@Components/ui-v2/button';
import { ConnectorInstructions } from '@Models';
import ChainAvatar, { ChainAvatarProps } from './chainAvatar';

export type ChainStatus = 'connected' | 'expiring' | 'expired' | 'available';

interface BaseChainCardProps {
  name: string;
  platform: string;
  avatar: Omit<ChainAvatarProps, 'size'>;
  lastSyncLabel: string;
  /** Username/email shown in the User meta row. */
  userLabel?: string;
  /** i18n key for the user row label; defaults to 'Credentials.Card.User'. */
  userLabelKey?: string;
  className?: string;
  /** Optional connector instructions shown when the help button is toggled on. */
  instructions?: ConnectorInstructions;
  /** Controlled flag: the parent decides whether the info panel is visible. */
  isInfoOpen?: boolean;
  /** Called when the user clicks the help button — parent flips its single-open state. */
  onToggleInfo?: () => void;
  /** When provided, replaces the instructions/action button area with the form. */
  formContent?: ReactNode;
  /** Platform-managed credential: no edit/test/docs actions shown. */
  readOnly?: boolean;
  /** Called when the user clicks "Editar". */
  onEdit?: () => void;
}

interface ConnectedCardProps extends BaseChainCardProps {
  status: 'connected';
  isSyncing?: boolean;
  onSync?: () => void;
}

interface ExpiringCardProps extends BaseChainCardProps {
  status: 'expiring' | 'expired';
  warningMessage: string;
  onUpdateCredentials?: () => void;
  onSync?: () => void;
}

interface AvailableCardProps extends BaseChainCardProps {
  status: 'available';
  onConnect?: () => void;
}

export type ConnectedChainCardProps = ConnectedCardProps | ExpiringCardProps | AvailableCardProps;

type InternalProps = BaseChainCardProps & {
  status: ChainStatus;
  warningMessage?: string;
  formContent?: ReactNode;
  isSyncing?: boolean;
  onSync?: () => void;
  onUpdateCredentials?: () => void;
  onConnect?: () => void;
};

export const STATUS_STYLES: Record<ChainStatus, { badge: string; dot: string }> = {
  connected: {
    badge: 'bg-success-50 text-success-700 border-success-100',
    dot: 'bg-success-500',
  },
  expiring: {
    badge: 'bg-warning-50 text-warning-800 border-warning-200',
    dot: 'bg-warning-500',
  },
  expired: {
    badge: 'bg-error-50 text-error-700 border-error-200',
    dot: 'bg-error-500',
  },
  available: {
    badge: 'bg-brand-50 text-brand-700 border-brand-100',
    dot: 'bg-brand-500',
  },
};

const STATUS_LABEL_KEY: Record<ChainStatus, string> = {
  connected: 'Credentials.Card.StatusConnected',
  expiring: 'Credentials.Card.StatusExpiring',
  expired: 'Credentials.Card.StatusExpired',
  available: 'Credentials.Card.StatusAvailable',
};

const ConnectedHeartbeatDot = ({ dotClass }: { dotClass: string }) => (
  <span className="relative inline-flex h-1.5 w-1.5 items-center justify-center">
    <span className={cn('absolute inset-0 animate-ping rounded-full [animation-duration:2s]', dotClass)} />
    <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', dotClass)} />
  </span>
);

export const StatusBadge = ({ status }: { status: ChainStatus }) => {
  const { t } = useTranslation();
  const styles = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        styles.badge,
      )}>
      {(status === 'expiring' || status === 'expired') && <AlertTriangle className="h-3 w-3" strokeWidth={2.25} />}
      {status === 'connected' && <ConnectedHeartbeatDot dotClass={styles.dot} />}
      {t(STATUS_LABEL_KEY[status])}
    </span>
  );
};

const HelpToggleButton = ({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('Credentials.Card.Help')}
      aria-expanded={isOpen}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors',
        isOpen
          ? 'border-brand-200 bg-brand-50 text-brand-700'
          : 'border-gray-200 bg-white text-gray-400 hover:border-brand-200 hover:text-brand-600',
      )}>
      <HelpCircle className="h-4 w-4" strokeWidth={2} />
    </button>
  );
};

const InstructionsPanel = ({ instructions, onClose }: { instructions: ConnectorInstructions; onClose: () => void }) => {
  const { t } = useTranslation();
  const title = instructions.kind === 'steps' ? instructions.title : t('Credentials.Instructions.GuideLabel');
  return (
    <div className="relative rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <h4 className="pr-6 text-sm font-semibold text-gray-900">{title}</h4>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('Global.Close')}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700">
          <X className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      </div>
      {instructions.kind === 'link' ? (
        <a
          href={instructions.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline">
          {t('Credentials.Instructions.OpenGuide')}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : (
        <>
          <ol className="flex flex-col gap-3">
            {instructions.steps.map((step, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={idx} className="flex min-w-0 gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[12px] font-semibold text-brand-700">
                  {idx + 1}
                </span>
                <span className="min-w-0 break-words text-sm leading-relaxed text-gray-700">{step}</span>
              </li>
            ))}
          </ol>
          {instructions.note && (
            <div className="mt-4 rounded-md bg-brand-50 p-3 text-xs leading-relaxed text-brand-700">
              <span className="font-semibold">{`${t('Credentials.Instructions.NoteLabel')}: `}</span>
              {instructions.note}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Sync action is hidden until the real per-connector sync endpoint lands —
// the 1.2s placeholder was misleading. Connected cards currently render no
// action button. Re-wire isSyncing/onSync from InternalProps and restore
// ConnectedActions when bopsApi.postSyncCredential exists:
//
// const ConnectedActions = ({ onSync, isSyncing }: { onSync?: () => void; isSyncing?: boolean }) => {
//   const { t } = useTranslation();
//   return (
//     <div className="flex items-center gap-2">
//       <Button
//         variant="outline"
//         size="sm"
//         className="h-9 flex-1 border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-100"
//         onClick={onSync}
//         disabled={isSyncing}>
//         <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isSyncing && 'animate-spin text-brand-600')} />
//         {isSyncing ? t('Credentials.Card.Syncing') : t('Credentials.Card.Sync')}
//       </Button>
//       <span
//         className="inline-flex h-9 w-9 items-center justify-center text-gray-300"
//         aria-label={t('Credentials.Card.Lock')}
//         role="img">
//         <Lock className="h-3.5 w-3.5" />
//       </span>
//     </div>
//   );
// };

const ConnectedChainCard = ({
  name,
  platform,
  avatar,
  lastSyncLabel,
  userLabel,
  userLabelKey,
  status,
  className,
  warningMessage,
  instructions,
  isInfoOpen = false,
  onToggleInfo,
  formContent,
  readOnly = false,
  onEdit,
  onUpdateCredentials,
  onConnect,
}: InternalProps) => {
  const { t } = useTranslation();
  const showInfo = isInfoOpen && !!instructions;
  const showForm = !!formContent;
  const showFooter = !readOnly;
  const resolvedOnEdit = onEdit ?? onUpdateCredentials;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-xl border bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
        status === 'available' ? 'border-dashed border-gray-200' : 'border-gray-200',
        className,
      )}>
      {/* Header: avatar + name + platform + status badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <ChainAvatar {...avatar} size="md" />
          <div className="flex flex-col leading-tight">
            <span className="text-[15px] font-semibold text-gray-900">{name}</span>
            <span className="text-xs text-gray-500">{platform}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!readOnly && instructions && onToggleInfo && <HelpToggleButton isOpen={isInfoOpen} onClick={onToggleInfo} />}
          <StatusBadge status={status} />
        </div>
      </div>

      {/* Meta section */}
      {status === 'available' ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5">
          <Plus className="h-3.5 w-3.5 shrink-0 text-gray-400" strokeWidth={2} />
          <span className="text-xs text-gray-500">{t('Credentials.Card.AvailableHint')}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {userLabel && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">{t(userLabelKey ?? 'Credentials.Card.User')}</span>
              <span className="max-w-[55%] truncate font-mono font-medium text-gray-700">{userLabel}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">{t('Credentials.Card.LastSync')}</span>
            <span className="font-medium text-gray-700">{lastSyncLabel}</span>
          </div>
        </div>
      )}

      {/* Warning banner for expiring/expired */}
      {(status === 'expiring' || status === 'expired') && warningMessage && (
        <div
          className={cn(
            'flex gap-2 rounded-md border px-3 py-2 text-xs',
            status === 'expired'
              ? 'border-error-200 bg-error-50 text-error-900'
              : 'border-warning-200 bg-warning-50 text-warning-900',
          )}>
          <AlertTriangle
            className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', status === 'expired' ? 'text-error-600' : 'text-warning-600')}
            strokeWidth={2.25}
          />
          <p className="leading-snug">{warningMessage}</p>
        </div>
      )}

      {/* Expandable info panel */}
      {showInfo && instructions && (
        <InstructionsPanel instructions={instructions} onClose={onToggleInfo ?? (() => {})} />
      )}

      {/* Inline edit/connect form */}
      {showForm && formContent}

      {/* Footer: doc button + actions */}
      {showFooter && (
        <div className="flex items-center gap-2 border-t border-gray-100 pt-3.5">
          <div className="flex-1" />
          {status === 'available' ? (
            <Button size="sm" className="h-9 bg-brand-600 text-white hover:bg-brand-700" onClick={onConnect}>
              {t('Credentials.Card.Connect')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              onClick={resolvedOnEdit}>
              <Settings2 className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
              {t('Credentials.Card.Edit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectedChainCard;
